import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { api } from './index';

// ==================== SCRAPE MODAL ====================
export const ScrapeModal = ({ isOpen, onClose, token, state, district, onComplete }) => {
  const { language } = useLanguage();
  const [selectedSource, setSelectedSource] = useState('cbse_saras');
  const [background, setBackground] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  const sources = [
    { id: 'cbse_saras', name: 'CBSE SARAS', nameHi: 'सीबीएसई सारस', desc: 'All India CBSE Schools', icon: '📚' },
    { id: 'mp_board', name: 'MP Board', nameHi: 'एमपी बोर्ड', desc: 'MP State Board Schools', icon: '📖' },
    { id: 'udise', name: 'UDISE+ Portal', nameHi: 'यूडाइस+', desc: 'All Schools with UDISE Code', icon: '🏛️' }
  ];

  const handleScrape = async () => {
    setLoading(true);
    setResult(null);
    setJobStatus(null);

    try {
      const res = await api.post('/api/school-finder/scrape', {
        source: selectedSource,
        state: state,
        district: district || null,
        background: background
      }, token);

      if (res.success) {
        if (res.cached) {
          setResult({ ...res.result, cached: true, scraped_at: res.scraped_at });
        } else if (background && res.job_id) {
          setJobStatus({ id: res.job_id, status: 'running' });
          pollJobStatus(res.job_id);
        } else {
          setResult(res.result);
          onComplete();
        }
      }
    } catch (err) {
      setResult({ error: 'Scraping failed' });
    }
    setLoading(false);
  };

  const pollJobStatus = async (jobId) => {
    const interval = setInterval(async () => {
      const res = await api.get(`/api/school-finder/jobs/${jobId}`, token);
      if (res.success && res.job) {
        setJobStatus(res.job);
        if (res.job.status === 'completed' || res.job.status === 'failed') {
          clearInterval(interval);
          if (res.job.status === 'completed') {
            setResult(res.job.result);
            onComplete();
          }
        }
      }
    }, 3000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex justify-between items-center p-4 border-b bg-blue-50">
          <h3 className="text-lg font-semibold">
            🌐 {language === 'hi' ? 'Schools Scrape करें' : 'Scrape Schools'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm"><strong>{language === 'hi' ? 'राज्य' : 'State'}:</strong> {state}</p>
            {district && <p className="text-sm"><strong>{language === 'hi' ? 'जिला' : 'District'}:</strong> {district}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {language === 'hi' ? 'Data Source चुनें' : 'Select Data Source'}
            </label>
            <div className="space-y-2">
              {sources.map(src => (
                <label key={src.id} className={`flex items-center p-3 border rounded-lg cursor-pointer transition ${selectedSource === src.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}>
                  <input type="radio" name="source" value={src.id} checked={selectedSource === src.id} onChange={(e) => setSelectedSource(e.target.value)} className="mr-3" />
                  <span className="text-2xl mr-3">{src.icon}</span>
                  <div>
                    <p className="font-medium">{language === 'hi' ? src.nameHi : src.name}</p>
                    <p className="text-xs text-gray-500">{src.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg">
            <input type="checkbox" checked={background} onChange={(e) => setBackground(e.target.checked)} className="w-4 h-4" />
            <div>
              <p className="text-sm font-medium">{language === 'hi' ? 'Background में चलाएं' : 'Run in Background'}</p>
              <p className="text-xs text-gray-500">{language === 'hi' ? 'बड़े data के लिए recommended' : 'Recommended for large data'}</p>
            </div>
          </label>

          {jobStatus && (
            <div className={`p-3 rounded-lg ${jobStatus.status === 'running' ? 'bg-blue-50' : jobStatus.status === 'completed' ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-center gap-2">
                {jobStatus.status === 'running' && <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>}
                <p className="font-medium">Status: {jobStatus.status.toUpperCase()}</p>
              </div>
              {jobStatus.progress && (
                <p className="text-sm mt-1">Found: {jobStatus.progress.total_found || 0} | Saved: {jobStatus.progress.new_saved || 0}</p>
              )}
            </div>
          )}

          {result && (
            <div className={`p-3 rounded-lg ${result.error ? 'bg-red-50' : 'bg-green-50'}`}>
              {result.cached && <p className="text-sm text-yellow-700 mb-2">⚡ {language === 'hi' ? 'Cached data' : 'Cached data'}</p>}
              {result.error ? (
                <p className="text-red-700">❌ {result.error}</p>
              ) : (
                <div className="text-sm space-y-1">
                  <p>✅ <strong>{language === 'hi' ? 'कुल मिले' : 'Total Found'}:</strong> {result.total_found || 0}</p>
                  <p>💾 <strong>{language === 'hi' ? 'नए Save' : 'New Saved'}:</strong> {result.new_saved || 0}</p>
                  <p>🔄 <strong>{language === 'hi' ? 'Update' : 'Updated'}:</strong> {result.updated || 0}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">{language === 'hi' ? 'बंद करें' : 'Close'}</button>
          {!result && (
            <button onClick={handleScrape} disabled={loading || (jobStatus?.status === 'running')} className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50">
              {loading ? '⏳ Loading...' : (language === 'hi' ? '🚀 शुरू करें' : '🚀 Start Scrape')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


// ==================== PDF UPLOAD MODAL ====================
export const PDFUploadModal = ({ isOpen, onClose, token, states, onComplete }) => {
  const { language } = useLanguage();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [state, setState] = useState('MADHYA PRADESH');
  const [district, setDistrict] = useState('');
  const [board, setBoard] = useState('MP BOARD');
  const [districts, setDistricts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (state) loadDistricts();
  }, [state]);

  const loadDistricts = async () => {
    const res = await api.get(`/api/school-finder/districts/${state}`, token);
    if (res.success) setDistricts(res.districts || []);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setResult(null);
    } else {
      alert(language === 'hi' ? 'सिर्फ PDF file allowed है' : 'Only PDF files are allowed');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const params = new URLSearchParams();
      params.append('state', state);
      if (district) params.append('district', district);
      params.append('board', board);

      const res = await api.uploadFile(`/api/school-finder/upload-pdf?${params}`, formData, token);
      if (res.success) {
        setResult(res.result);
        onComplete();
      } else {
        setResult({ error: res.detail || 'Upload failed' });
      }
    } catch (err) {
      setResult({ error: 'Upload failed' });
    }
    setUploading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex justify-between items-center p-4 border-b bg-purple-50">
          <h3 className="text-lg font-semibold">📄 {language === 'hi' ? 'PDF Upload करें' : 'Upload PDF'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        <div className="p-4 space-y-4">
          <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-purple-400 transition">
            <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" />
            {selectedFile ? (
              <div>
                <span className="text-4xl">📄</span>
                <p className="mt-2 font-medium">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
              </div>
            ) : (
              <div>
                <span className="text-4xl">📤</span>
                <p className="mt-2 text-gray-600">{language === 'hi' ? 'PDF file चुनने के लिए click करें' : 'Click to select PDF file'}</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'राज्य' : 'State'}</label>
              <select value={state} onChange={(e) => { setState(e.target.value); setDistrict(''); }} className="w-full px-3 py-2 border rounded-lg">
                {states.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{language === 'hi' ? 'जिला' : 'District'}</label>
              <select value={district} onChange={(e) => setDistrict(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                <option value="">{language === 'hi' ? 'सभी' : 'All'}</option>
                {districts.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Board</label>
            <select value={board} onChange={(e) => setBoard(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
              <option value="MP BOARD">MP Board</option>
              <option value="CBSE">CBSE</option>
              <option value="ICSE">ICSE</option>
              <option value="STATE BOARD">State Board</option>
            </select>
          </div>

          {result && (
            <div className={`p-3 rounded-lg ${result.error ? 'bg-red-50' : 'bg-green-50'}`}>
              {result.error ? (
                <p className="text-red-700">❌ {result.error}</p>
              ) : (
                <div className="text-sm space-y-1">
                  <p>📄 <strong>Pages:</strong> {result.pages_processed || 0}</p>
                  <p>✅ <strong>{language === 'hi' ? 'कुल मिले' : 'Total Found'}:</strong> {result.total_found || 0}</p>
                  <p>💾 <strong>{language === 'hi' ? 'नए Save' : 'New Saved'}:</strong> {result.new_saved || 0}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">{language === 'hi' ? 'बंद करें' : 'Close'}</button>
          {!result && (
            <button onClick={handleUpload} disabled={!selectedFile || uploading} className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50">
              {uploading ? '⏳ Processing...' : '📤 Upload & Extract'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


// ==================== SCHOOL FORM MODAL ====================
export const SchoolFormModal = ({ isOpen, onClose, school, onSave, states, token }) => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    school_name: '', school_name_hindi: '', state: 'MADHYA PRADESH', district: '',
    board: 'CBSE', permission_level: 'senior_secondary', total_students: '',
    principal_name: '', principal_phone: '', contact_person_name: '', contact_person_designation: '',
    email: '', phone: '', address: '', website: '', affiliation_number: '', notes: '',
    whatsapp_number: '', mobile_number: '', landline_number: ''
  });
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (school) {
      setFormData({
        school_name: school.school_name || '', school_name_hindi: school.school_name_hindi || '',
        state: school.state || 'MADHYA PRADESH', district: school.district || '',
        board: school.board || 'CBSE', permission_level: school.permission_level || 'senior_secondary',
        total_students: school.total_students || '', principal_name: school.principal_name || '',
        principal_phone: school.principal_phone || '', contact_person_name: school.contact_person_name || '',
        contact_person_designation: school.contact_person_designation || '', email: school.email || '',
        phone: school.phone || '', address: school.address || '',
        website: school.website || '', affiliation_number: school.affiliation_number || '', notes: school.notes || '',
        whatsapp_number: school.whatsapp_number || '', mobile_number: school.mobile_number || '', landline_number: school.landline_number || ''
      });
    } else {
      setFormData({
        school_name: '', school_name_hindi: '', state: 'MADHYA PRADESH', district: '',
        board: 'CBSE', permission_level: 'senior_secondary', total_students: '',
        principal_name: '', principal_phone: '', contact_person_name: '', contact_person_designation: '',
        email: '', phone: '', address: '', website: '', affiliation_number: '', notes: '',
        whatsapp_number: '', mobile_number: '', landline_number: ''
      });
    }
    setError('');
  }, [school, isOpen]);

  useEffect(() => {
    const load = async () => {
      if (formData.state) {
        const res = await api.get(`/api/school-finder/districts/${formData.state}`, token);
        setDistricts(res.districts || []);
      }
    };
    load();
  }, [formData.state, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.whatsapp_number && !formData.mobile_number && !formData.phone && !formData.email) {
      setError(language === 'hi' ? 'WhatsApp/Mobile/Phone या Email में से कोई एक जरूरी है' : 'At least one contact method required');
      return;
    }
    setLoading(true);
    const result = await onSave(formData);
    if (result.success) onClose();
    else setError(result.detail || 'Error');
    setLoading(false);
  };

  if (!isOpen) return null;

  const levels = [
    { id: 'play_school', label: 'Play School' }, { id: 'primary', label: 'Primary (1-5)' },
    { id: 'middle', label: 'Middle (6-8)' }, { id: 'secondary', label: 'Secondary (10)' },
    { id: 'senior_secondary', label: 'Sr. Secondary (12)' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b bg-orange-50">
          <h3 className="text-lg font-semibold">{school ? '✏️ Edit School' : '➕ Add School'}</h3>
          <button onClick={onClose} className="text-gray-500 text-2xl">&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">{error}</div>}
          
          <div className="grid grid-cols-2 gap-3">
            <input placeholder={language === 'hi' ? "स्कूल का नाम *" : "School Name *"} required value={formData.school_name} onChange={(e) => setFormData({...formData, school_name: e.target.value})} className="px-3 py-2 border rounded-lg" data-testid="school-name-input" />
            <input placeholder="स्कूल का नाम (Hindi)" value={formData.school_name_hindi} onChange={(e) => setFormData({...formData, school_name_hindi: e.target.value})} className="px-3 py-2 border rounded-lg" />
            
            <select value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value, district: ''})} className="px-3 py-2 border rounded-lg" required>
              {states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={formData.district} onChange={(e) => setFormData({...formData, district: e.target.value})} className="px-3 py-2 border rounded-lg" required>
              <option value="">{language === 'hi' ? 'जिला चुनें *' : 'Select District *'}</option>
              {districts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            
            <select value={formData.board} onChange={(e) => setFormData({...formData, board: e.target.value})} className="px-3 py-2 border rounded-lg">
              <option value="CBSE">CBSE</option>
              <option value="MP BOARD">MP Board</option>
              <option value="ICSE">ICSE</option>
            </select>
            <select value={formData.permission_level} onChange={(e) => setFormData({...formData, permission_level: e.target.value})} className="px-3 py-2 border rounded-lg">
              {levels.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
            
            <input placeholder="Affiliation Number" value={formData.affiliation_number} onChange={(e) => setFormData({...formData, affiliation_number: e.target.value})} className="px-3 py-2 border rounded-lg" />
            <input placeholder={language === 'hi' ? "कुल छात्र" : "Total Students"} type="number" value={formData.total_students} onChange={(e) => setFormData({...formData, total_students: e.target.value})} className="px-3 py-2 border rounded-lg" />
            
            <div className="col-span-2 border-t pt-3 mt-2">
              <p className="text-sm font-medium text-gray-700 mb-2">{language === 'hi' ? '📞 संपर्क व्यक्ति की जानकारी' : '📞 Contact Person Details'}</p>
            </div>
            
            <input placeholder={language === 'hi' ? "संपर्क व्यक्ति का नाम" : "Contact Person Name"} value={formData.contact_person_name} onChange={(e) => setFormData({...formData, contact_person_name: e.target.value})} className="px-3 py-2 border rounded-lg" data-testid="contact-person-name-input" />
            <select value={formData.contact_person_designation} onChange={(e) => setFormData({...formData, contact_person_designation: e.target.value})} className="px-3 py-2 border rounded-lg" data-testid="contact-person-designation-select">
              <option value="">{language === 'hi' ? 'पद चुनें' : 'Select Designation'}</option>
              <option value="Principal">{language === 'hi' ? 'प्रिंसिपल' : 'Principal'}</option>
              <option value="Vice Principal">{language === 'hi' ? 'वाइस प्रिंसिपल' : 'Vice Principal'}</option>
              <option value="Director">{language === 'hi' ? 'डायरेक्टर' : 'Director'}</option>
              <option value="Coordinator">{language === 'hi' ? 'कोऑर्डिनेटर' : 'Coordinator'}</option>
              <option value="Admin">{language === 'hi' ? 'एडमिन' : 'Admin'}</option>
              <option value="Manager">{language === 'hi' ? 'मैनेजर' : 'Manager'}</option>
              <option value="Teacher">{language === 'hi' ? 'शिक्षक' : 'Teacher'}</option>
              <option value="Other">{language === 'hi' ? 'अन्य' : 'Other'}</option>
            </select>
            
            <input placeholder={language === 'hi' ? "प्रिंसिपल का नाम" : "Principal Name"} value={formData.principal_name} onChange={(e) => setFormData({...formData, principal_name: e.target.value})} className="px-3 py-2 border rounded-lg" />
            <input placeholder={language === 'hi' ? "प्रिंसिपल का फोन" : "Principal Phone"} value={formData.principal_phone} onChange={(e) => setFormData({...formData, principal_phone: e.target.value})} className="px-3 py-2 border rounded-lg" maxLength="10" />
            
            <div className="col-span-2 border-t pt-3 mt-2">
              <p className="text-sm font-medium text-gray-700 mb-2">{language === 'hi' ? '📱 फ़ोन नंबर' : '📱 Phone Numbers'}</p>
            </div>
            
            <div className="relative">
              <span className="absolute left-3 top-2 text-green-500">💬</span>
              <input placeholder={language === 'hi' ? "WhatsApp नंबर" : "WhatsApp Number"} value={formData.whatsapp_number} onChange={(e) => setFormData({...formData, whatsapp_number: e.target.value})} className="px-3 py-2 pl-10 border rounded-lg border-green-300 w-full" maxLength="10" data-testid="whatsapp-number-input" />
            </div>
            <div className="relative">
              <span className="absolute left-3 top-2 text-blue-500">📱</span>
              <input placeholder={language === 'hi' ? "मोबाइल नंबर" : "Mobile Number"} value={formData.mobile_number} onChange={(e) => setFormData({...formData, mobile_number: e.target.value})} className="px-3 py-2 pl-10 border rounded-lg border-blue-300 w-full" maxLength="10" data-testid="mobile-number-input" />
            </div>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">☎️</span>
              <input placeholder={language === 'hi' ? "लैंडलाइन नंबर" : "Landline Number"} value={formData.landline_number} onChange={(e) => setFormData({...formData, landline_number: e.target.value})} className="px-3 py-2 pl-10 border rounded-lg w-full" data-testid="landline-number-input" />
            </div>
            
            <input placeholder="Email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="px-3 py-2 border rounded-lg" data-testid="school-email-input" />
            
            <textarea placeholder={language === 'hi' ? "पता" : "Address"} value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="col-span-2 px-3 py-2 border rounded-lg" rows="2" />
          </div>

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={loading} className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50">
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ==================== SEND PROPOSAL MODAL ====================
export const SendProposalModal = ({ isOpen, onClose, school, token }) => {
  const { language } = useLanguage();
  const [channel, setChannel] = useState('whatsapp');
  const [templates, setTemplates] = useState([]);
  const [customMessage, setCustomMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      setResult(null);
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    const res = await api.get('/api/school-finder/templates', token);
    if (res.success) {
      setTemplates(res.templates || []);
      const t = res.templates?.find(t => t.channel === channel);
      if (t) setCustomMessage(t.body);
    }
  };

  useEffect(() => {
    const t = templates.find(t => t.channel === channel);
    if (t) setCustomMessage(t.body);
  }, [channel, templates]);

  const handleSend = async () => {
    setLoading(true);
    const res = await api.post('/api/school-finder/send-proposal', {
      school_id: school.id || school.affiliation_number,
      channel, custom_message: customMessage
    }, token);
    if (res.success) setResult(res);
    setLoading(false);
  };

  if (!isOpen || !school) return null;

  const preview = customMessage
    .replace('{principal_name}', school.principal_name || 'Sir/Madam')
    .replace('{school_name}', school.school_name || '');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b bg-green-50">
          <h3 className="text-lg font-semibold">📤 Send Proposal</h3>
          <button onClick={onClose} className="text-gray-500 text-2xl">&times;</button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)] space-y-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="font-semibold">{school.school_name}</p>
            <p className="text-sm text-gray-600">{school.district}, {school.state}</p>
            <div className="flex gap-4 mt-2 text-sm">
              {school.phone && <span>📞 {school.phone}</span>}
              {school.email && <span>📧 {school.email}</span>}
            </div>
          </div>

          <div className="flex gap-3">
            <button onClick={() => setChannel('whatsapp')} className={`flex-1 py-3 rounded-lg ${channel === 'whatsapp' ? 'bg-green-500 text-white' : 'bg-gray-100'}`}>💬 WhatsApp</button>
            <button onClick={() => setChannel('email')} className={`flex-1 py-3 rounded-lg ${channel === 'email' ? 'bg-blue-500 text-white' : 'bg-gray-100'}`}>📧 Email</button>
          </div>

          <textarea value={customMessage} onChange={(e) => setCustomMessage(e.target.value)} className="w-full px-3 py-2 border rounded-lg font-mono text-sm" rows="8" />

          <div className="bg-gray-100 rounded-lg p-3 whitespace-pre-wrap text-sm max-h-32 overflow-y-auto">{preview}</div>

          {result && (
            <div className="p-4 bg-green-50 rounded-lg">
              {channel === 'whatsapp' ? (
                <a href={result.whatsapp_link} target="_blank" rel="noopener noreferrer" className="inline-block px-4 py-2 bg-green-500 text-white rounded-lg">💬 Open WhatsApp</a>
              ) : (
                <a href={`mailto:${result.email_to}?subject=${encodeURIComponent(result.email_subject)}&body=${encodeURIComponent(result.email_body)}`} className="inline-block px-4 py-2 bg-blue-500 text-white rounded-lg">📧 Open Email</a>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Close</button>
          {!result && (
            <button onClick={handleSend} disabled={loading} className={`px-6 py-2 text-white rounded-lg ${channel === 'whatsapp' ? 'bg-green-500' : 'bg-blue-500'}`}>
              {loading ? 'Preparing...' : 'Send'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


// ==================== URL SCRAPE MODAL ====================
export const URLScrapeModal = ({ isOpen, onClose, token, onSuccess }) => {
  const { language } = useLanguage();
  const [url, setUrl] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [mode, setMode] = useState('url');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [bulkState, setBulkState] = useState('');
  const [bulkDistrict, setBulkDistrict] = useState('');
  const [bulkBoard, setBulkBoard] = useState('CBSE');

  useEffect(() => {
    if (isOpen) loadHistory();
  }, [isOpen]);

  const loadHistory = async () => {
    const res = await api.get('/api/school-finder/scrape-history?limit=10', token);
    if (res.success) setHistory(res.history || []);
  };

  const handleScrape = async () => {
    if (!url.trim()) return alert(language === 'hi' ? 'URL डालें' : 'Enter URL');
    setLoading(true);
    setResult(null);
    
    const res = await api.post('/api/school-finder/scrape-url', { url: url.trim() }, token);
    setResult(res);
    setLoading(false);
    
    if (res.success) {
      loadHistory();
      if (onSuccess) onSuccess();
    }
  };

  const handleBulkImport = async () => {
    if (!bulkText.trim()) return alert(language === 'hi' ? 'Data paste करें' : 'Paste data');
    setLoading(true);
    setResult(null);
    
    const res = await api.post('/api/school-finder/bulk-import-text', { 
      data: bulkText.trim(), state: bulkState, district: bulkDistrict, board: bulkBoard
    }, token);
    
    setResult(res);
    setLoading(false);
    if (res.success && onSuccess) onSuccess();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-800">🔗 {language === 'hi' ? 'Data Import करें' : 'Import Data'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setMode('url')} className={`px-4 py-2 rounded-lg font-medium ${mode === 'url' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-700'}`}>🌐 URL Scrape</button>
          <button onClick={() => setMode('bulk')} className={`px-4 py-2 rounded-lg font-medium ${mode === 'bulk' ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-700'}`}>📋 Bulk Paste</button>
        </div>

        {mode === 'url' ? (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">{language === 'hi' ? 'Scribd, Google Sheets या कोई भी URL paste करें' : 'Paste Scribd, Google Sheets or any URL'}</label>
              <div className="flex gap-2">
                <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.scribd.com/document/..." className="flex-1 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" data-testid="url-scrape-input" />
                <button onClick={handleScrape} disabled={loading || !url.trim()} className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 font-medium" data-testid="url-scrape-submit">
                  {loading ? '⏳' : '🚀'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-purple-800 mb-2">📋 {language === 'hi' ? 'Supported Formats' : 'Supported Formats'}:</p>
              <ul className="text-purple-700 space-y-1 text-xs">
                <li>• CSV/TSV: School Name, Principal, Phone, Email</li>
                <li>• CISCE: UP001ST. GEORGE'S COLLEGE...09319100789head@up001cisce.org</li>
                <li>• Simple: School Name | Phone</li>
              </ul>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <select value={bulkState} onChange={(e) => setBulkState(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
                <option value="">{language === 'hi' ? 'State चुनें' : 'Select State'}</option>
                <option value="MADHYA PRADESH">Madhya Pradesh</option>
                <option value="UTTAR PRADESH">Uttar Pradesh</option>
                <option value="MAHARASHTRA">Maharashtra</option>
                <option value="RAJASTHAN">Rajasthan</option>
                <option value="DELHI">Delhi</option>
              </select>
              <input type="text" value={bulkDistrict} onChange={(e) => setBulkDistrict(e.target.value.toUpperCase())} placeholder="District" className="px-3 py-2 border rounded-lg text-sm" />
              <select value={bulkBoard} onChange={(e) => setBulkBoard(e.target.value)} className="px-3 py-2 border rounded-lg text-sm">
                <option value="CBSE">CBSE</option>
                <option value="ICSE">ICSE</option>
                <option value="STATE">State Board</option>
              </select>
            </div>

            <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={language === 'hi' ? "Scribd से copy किया हुआ data यहाँ paste करें..." : "Paste copied data from Scribd here..."} className="w-full h-48 px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm font-mono" data-testid="bulk-import-textarea" />

            <button onClick={handleBulkImport} disabled={loading || !bulkText.trim()} className="w-full px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 font-medium" data-testid="bulk-import-submit">
              {loading ? '⏳ Processing...' : '📥 Import Data'}
            </button>
          </div>
        )}

        {result && (
          <div className={`mt-4 p-4 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            {result.success ? (
              <div>
                <p className="font-bold text-green-800 text-lg mb-2">✅ {result.message_hi || 'Success!'}</p>
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div className="bg-white p-2 rounded"><p className="text-2xl font-bold text-gray-800">{result.stats?.total_found || 0}</p><p className="text-xs text-gray-600">{language === 'hi' ? 'मिले' : 'Found'}</p></div>
                  <div className="bg-white p-2 rounded"><p className="text-2xl font-bold text-green-600">{result.stats?.new_saved || 0}</p><p className="text-xs text-gray-600">{language === 'hi' ? 'नए' : 'New'}</p></div>
                  <div className="bg-white p-2 rounded"><p className="text-2xl font-bold text-blue-600">{result.stats?.enhanced || 0}</p><p className="text-xs text-gray-600">Enhanced</p></div>
                  <div className="bg-white p-2 rounded"><p className="text-2xl font-bold text-orange-600">{result.stats?.updated || 0}</p><p className="text-xs text-gray-600">Updated</p></div>
                </div>
              </div>
            ) : (
              <p className="text-red-700">{result.message_hi || result.message || result.detail}</p>
            )}
          </div>
        )}

        {mode === 'url' && (
          <>
            <div className="flex items-center justify-between mt-4">
              <button onClick={() => setShowHistory(!showHistory)} className="text-sm text-blue-600 hover:underline">
                {showHistory ? '▼' : '▶'} History ({history.length})
              </button>
              {history.length > 0 && (
                <button onClick={async () => {
                  if (window.confirm(language === 'hi' ? 'सभी history clear करें?' : 'Clear all history?')) {
                    await api.delete('/api/school-finder/scrape-history/scribd', token);
                    loadHistory();
                  }
                }} className="text-xs text-red-500 hover:underline">
                  🗑️ {language === 'hi' ? 'History Clear करें' : 'Clear History'}
                </button>
              )}
            </div>

            {showHistory && history.length > 0 && (
              <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">URL</th>
                      <th className="px-2 py-1 text-center">Found</th>
                      <th className="px-2 py-1 text-center">New</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={i} className="border-t hover:bg-gray-50">
                        <td className="px-2 py-1 truncate max-w-[200px]" title={h.url}>{h.url}</td>
                        <td className="px-2 py-1 text-center">{h.stats?.total_found || 0}</td>
                        <td className="px-2 py-1 text-center text-green-600">{h.stats?.new_saved || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">{language === 'hi' ? 'बंद करें' : 'Close'}</button>
        </div>
      </div>
    </div>
  );
};
